/** @jsx jsx */
import React, { Component } from 'react';
import { styled, css, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import SwitcherIcon from '@atlaskit/icon/core/migration/app-switcher--switcher';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import { token } from '@atlaskit/tokens';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type {
	Item,
	RenderTrigger,
} from '@atlassian/jira-navigation-apps-common/src/compontents/switcher/model/index.tsx';
import withFireUiAnalyticsFactory from '@atlassian/jira-navigation-apps-common/src/compontents/switcher/services/with-fire-ui-analytics-factory/index.tsx';
import Switcher from '@atlassian/jira-navigation-apps-common/src/compontents/switcher/view/switcher/view.tsx';
import Trigger from '@atlassian/jira-navigation-apps-common/src/compontents/switcher/view/trigger/view.tsx';
import type { Board } from '@atlassian/jira-navigation-types/src/types.tsx';
import { Link } from '@atlassian/react-resource-router';
import messages from './messages.tsx';

export type OnClick = (
	arg1: {
		id: string;
		isViewAll: boolean;
		sectionIndex: number;
		recentBoards: number;
		boardsInLocation: number;
	},
	arg2: UIAnalyticsEvent,
) => void;

export type FetchInitialBoards = () => void;
export type SearchBoards = (value: string) => void;
export type LoadMore = () => void;
export type OnRetry = () => void;

type Props = {
	isLoading: boolean;
	isLoadingMore: boolean;
	isLocationUser: boolean;
	isSearchUsed: boolean;
	isError: boolean;
	hasCreateBoardButton: boolean;
	hasShowMoreButton: boolean;
	remainingShowMoreBoards: number;
	searchValue: string;
	locationName: string;
	boardsInLocation: Board[];
	otherBoards: Board[];
	selectedBoard: Board | undefined;
	defaultOpen: boolean;
	viewAllUrl: string;
	fetchInitialBoards: FetchInitialBoards;
	searchBoards: SearchBoards;
	loadMore: LoadMore;
	onRetry: OnRetry;
	onCreateBoard: (arg1: UIAnalyticsEvent) => void;
	onClick: OnClick;
	intl: IntlShape;
};

const before = () => <BoardIcon label="" color="currentColor" />;

const transformBoardInLocation = (board: Board): Item => ({
	text: board.name,
	key: board.id,
	before,
	url: board.url,
});

const transformBoardNotInLocation = (board: Board, subText: string): Item => ({
	text: board.name,
	key: board.id,
	subText,
	before,
	url: board.url,
});

const getLastLetter = (name?: string): string => (name ? name.toLowerCase().slice(-1) : '');

const SwitcherWithAnalytics = withFireUiAnalytics(withFireUiAnalyticsFactory('board'))(Switcher);

// eslint-disable-next-line jira/react/no-class-components
export class ScopeSwitcherV2 extends Component<Props> {
	static defaultProps = {
		isLocationUser: false,
		isLoading: false,
		isLoadingMore: false,
		isSearchUsed: false,
		isError: false,
		hasCreateBoardButton: false,
		hasShowMoreButton: false,
		remainingShowMoreBoards: 0,
		defaultOpen: false,
		selectedBoard: undefined,
		searchValue: '',
		fetchInitialBoards: noop,
		searchBoards: noop,
		loadMore: noop,
		onCreateBoard: noop,
		onClick: noop,
		onRetry: noop,
	};

	onSearchChange = (value: string) => {
		const { searchBoards, fetchInitialBoards } = this.props;

		if (value && value.length) {
			searchBoards(value);
		} else {
			searchBoards('');
			fetchInitialBoards();
		}
	};

	onOpenChange: (arg1: { isOpen: boolean }) => void = ({ isOpen }) => {
		if (isOpen) {
			this.props.fetchInitialBoards();
		}
	};

	getCreateLabel() {
		const {
			hasCreateBoardButton,
			intl: { formatMessage },
		} = this.props;
		if (hasCreateBoardButton) {
			return formatMessage(messages.createBoard);
		}

		return undefined;
	}

	getBoardsInLocationLabel() {
		const {
			locationName,
			isLocationUser,
			intl: { formatMessage },
		} = this.props;

		if (this.areBoardsInLocationEmpty()) {
			return undefined;
		}

		if (!isLocationUser) {
			return formatMessage(messages.labelBoardsInProjectLocation, { locationName });
		}

		return formatMessage(messages.labelBoardsInUserLocationNew, {
			locationName,
			lastLetter: getLastLetter(locationName),
		});
	}

	getOtherBoardLocationDescription(board: Board) {
		const {
			intl: { formatMessage },
		} = this.props;

		const { locationName, isLocationUser } = board;

		if (!locationName) {
			return '';
		}

		if (!isLocationUser) {
			return formatMessage(messages.projectLocationDescription, {
				location: board.locationName,
			});
		}

		return formatMessage(messages.userLocationDescriptionNew, {
			locationName,
			lastLetter: getLastLetter(locationName),
		});
	}

	getListHeading() {
		const {
			locationName,
			isSearchUsed,
			boardsInLocation,
			otherBoards,
			intl: { formatMessage },
		} = this.props;

		const areThereEmptySearchResults =
			isSearchUsed && !(boardsInLocation.length + otherBoards.length);

		if (areThereEmptySearchResults) {
			return formatMessage(messages.searchNoMatchesFound);
		}

		if (!isSearchUsed && this.areBoardsInLocationEmpty()) {
			return formatMessage(messages.locationDoesNotHaveBoard, { location: locationName });
		}

		return undefined;
	}

	areBoardsInLocationEmpty() {
		return this.props.boardsInLocation.length === 0;
	}

	createOptions() {
		const {
			boardsInLocation,
			otherBoards,
			hasShowMoreButton,
			isLoadingMore,
			remainingShowMoreBoards,
			intl: { formatMessage },
		} = this.props;

		const curriedTransformBoardNotInLocation = (board: Board) =>
			transformBoardNotInLocation(board, this.getOtherBoardLocationDescription(board));

		const showMore = {
			isLoading: isLoadingMore,
			label: formatMessage(messages.showMore, {
				remainingBoards: remainingShowMoreBoards,
			}),
		};

		return [
			boardsInLocation.length && {
				label: this.getBoardsInLocationLabel(),
				options: boardsInLocation.map(transformBoardInLocation),
				...(hasShowMoreButton ? { showMore } : {}),
			},
			otherBoards.length && {
				label: this.createHeaderLabel(),
				options: otherBoards.map(curriedTransformBoardNotInLocation),
			},
		].filter(Boolean);
	}

	createViewAll() {
		const {
			intl: { formatMessage },
			viewAllUrl,
		} = this.props;

		return {
			withoutTopMargin: true,
			options: [
				{
					text: formatMessage(messages.viewAllBoards),
					key: '-1',
					url: viewAllUrl,
					isViewAll: true,
				},
			],
		};
	}

	createHeaderLabel() {
		const {
			intl: { formatMessage },
			viewAllUrl,
		} = this.props;

		return (
			<>
				<GroupHeadingLabel>{formatMessage(messages.otherRecentBoards)}</GroupHeadingLabel>
				<Link href={viewAllUrl} css={linkStyles}>
					{formatMessage(messages.viewAll)}
				</Link>
			</>
		);
	}

	renderTrigger: RenderTrigger = ({ isOpen, targetRef }) => {
		const {
			selectedBoard,
			intl: { formatMessage },
		} = this.props;

		return (
			<div
				data-test-id="navigation-apps.scope-switcher-v2"
				data-testid="navigation-apps.scope-switcher-v2"
			>
				<Trigger
					Icon={SwitcherIcon}
					text={selectedBoard ? selectedBoard.name : formatMessage(messages.boards)}
					subText={selectedBoard ? formatMessage(messages.board) : undefined}
					isOpen={isOpen}
					targetRef={targetRef}
				/>
			</div>
		);
	};

	render() {
		const {
			selectedBoard,
			defaultOpen,
			isLoading,
			isError,
			onCreateBoard,
			onClick,
			onRetry,
			searchValue,
			loadMore,
			intl: { formatMessage },
		} = this.props;

		if (__SERVER__) {
			return (
				<Trigger
					Icon={SwitcherIcon}
					text={selectedBoard ? selectedBoard.name : formatMessage(messages.boards)}
					subText={selectedBoard ? formatMessage(messages.board) : undefined}
				/>
			);
			// eslint-disable-next-line no-else-return
		} else {
			return (
				<SwitcherWithAnalytics
					// @ts-expect-error - Type '({ showMore?: { isLoading: boolean; label: string; } | undefined; label: string | undefined; options: Item[]; } | { label: Element; options: Item[]; })[]' is not assignable to type 'ItemGroup[]'.
					items={this.createOptions()}
					isLoading={isLoading}
					selectedItem={selectedBoard ? transformBoardInLocation(selectedBoard) : undefined}
					createLabel={this.getCreateLabel()}
					searchValue={searchValue}
					searchPlaceholder={formatMessage(messages.searchPlaceholder)}
					// @ts-expect-error - Type 'OnClick' is not assignable to type '(arg1: Item, sectionIndex: number, args_2: UIAnalyticsEvent) => void'.
					onClick={onClick}
					listHeading={this.getListHeading()}
					renderTrigger={this.renderTrigger}
					isDefaultOpen={defaultOpen}
					onOpenChange={this.onOpenChange}
					onCreate={onCreateBoard}
					isSearchEnabled
					onSearchChange={this.onSearchChange}
					onRetry={onRetry}
					isError={isError}
					errorMessage={formatMessage(messages.errorMessage)}
					onShowMore={loadMore}
				/>
			);
		}
	}
}

export default injectIntl(ScopeSwitcherV2);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupHeadingLabel = styled.p({
	flexGrow: 1,
	flexShrink: 1,
	overflow: 'hidden',
	paddingRight: token('space.100'),
	textOverflow: 'ellipsis',
});

const linkStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&&': {
		width: 'inherit',
		marginLeft: 0,
		marginRight: 0,
		paddingLeft: 0,
	},
});
